import React, { useMemo, useState } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex, Divider,
    Avatar, Timeline, Alert, Image, Breadcrumb, Progress
} from "antd";
import {
    BookOutlined, UserOutlined, CalendarOutlined, GlobalOutlined,
    EnvironmentOutlined, WarningOutlined, DollarOutlined, TeamOutlined,
    ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined,
    SafetyCertificateOutlined, HomeOutlined, ReadOutlined, EyeOutlined, ClockCircleOutlined,
    QuestionCircleOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PublicInvitationDetailDto, JoinRequestDto } from "../../commons/data/types";
import dayjs from "dayjs";
import { useAuthContext } from "../auth/LauncherAuthContext";
import { ReservationCtaSection } from "../../reservation/ReservationCtaSection";
import { SessionRsvpBookingForm } from "./SessionRsvpBookingForm";
import { ScheduleInvitationButton } from "./ScheduleInvitationButton";
import { Label } from "../../commons/translations/Label";

const { Title, Paragraph, Text } = Typography;

export const InvitationDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showBookingForm, setShowBookingForm] = useState<boolean>(false);

    const { user, isUserLoggedIn } = useAuthContext();

    const { data: detail, isLoading, isError } = useQuery<PublicInvitationDetailDto>(
        ['public-invitation-detail', id],
        () => axios.get(`/api/public/invitations/${id}`).then(res => res.data),
        { enabled: !!id }
    );

    const { data: userJoinRequest, isLoading: isUserJoinRequestLoading, refetch: refetchUserJoinRequest } = useQuery<JoinRequestDto | null>(
        ['my-join-request-for-invitation', id, isUserLoggedIn()],
        async () => {
            try {
                const res = await axios.get<JoinRequestDto>(`/api/launcher/my/join-requests/invitation/${id}`);
                if (res.data && typeof res.data === 'object' && res.data.id) {
                    return res.data;
                }
                return null;
            } catch {
                return null;
            }
        },
        { enabled: isUserLoggedIn() && !!id }
    );

    const hasUserJoinRequest = useMemo<boolean>(() => {
        return !!userJoinRequest && typeof userJoinRequest === 'object' && !!userJoinRequest.id;
    }, [userJoinRequest]);

    const isOrganizer = useMemo<boolean>(() => {
        if (!user || !detail) return false;
        return !!detail.organizerId && (user.organizer?.id === detail.organizerId || user.id === detail.organizerId);
    }, [user, detail]);

    const isGm = useMemo<boolean>(() => {
        if (!user || !detail) return false;
        return !!detail.gmId && user.id === detail.gmId;
    }, [user, detail]);

    const verifiedPlayers = useMemo<number>(() => {
        if (!detail) return 0;
        return Math.max(0, detail.maxTotalPlayers - detail.availableSeats);
    }, [detail]);

    const isOverMinimum = useMemo<boolean>(() => {
        if (!detail) return false;
        return verifiedPlayers >= detail.minTotalPlayers;
    }, [detail, verifiedPlayers]);

    const isAtMaximum = useMemo<boolean>(() => {
        if (!detail) return false;
        return verifiedPlayers >= detail.maxTotalPlayers;
    }, [detail, verifiedPlayers]);

    if (isLoading) {
        return (
            <LauncherLayout>
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" />
                </div>
            </LauncherLayout>
        );
    }

    if (isError || !detail) {
        return (
            <LauncherLayout>
                <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
                    <Alert
                        message={t('invitations.detail.error.title', 'Invitation Not Found')}
                        description={t('invitations.detail.error.desc', 'The requested adventure invitation could not be found or is no longer active.')}
                        type="error"
                        showIcon
                        action={
                            <Button type="primary" onClick={() => navigate('/invitations')}>
                                {t('invitations.backToBrowse', 'Back to Adventures')}
                            </Button>
                        }
                    />
                </div>
            </LauncherLayout>
        );
    }

    return (
        <LauncherLayout>
            <div style={{ maxWidth: '1100px', margin: '30px auto', padding: '0 20px' }}>
                {/* BREADCRUMB & BACK BUTTON */}
                <div style={{ marginBottom: 16 }}>
                    <Flex justify="space-between" align="center">
                        <Breadcrumb
                            items={[
                                { title: <Link to="/"><HomeOutlined /> {t('common.home', 'Home')}</Link> },
                                { title: <Link to="/invitations">{t('invitations.browse.nav', 'Adventures')}</Link> },
                                { title: detail.title }
                            ]}
                        />
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invitations')}>
                            {t('invitations.backToBrowse', 'Back to List')}
                        </Button>
                    </Flex>
                </div>

                {/* HERO BANNER CARD */}
                <Card
                    style={{
                        ...launcherCard,
                        marginBottom: 24,
                        overflow: 'hidden',
                        padding: 0
                    }}
                >
                    <Row gutter={[0, 0]}>
                        <Col xs={24} md={10}>
                            <div style={{ height: '100%', minHeight: 280, position: 'relative', background: '#111' }}>
                                {detail.coverUrl ? (
                                    <img
                                        src={detail.coverUrl}
                                        alt={detail.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <BookOutlined style={{ fontSize: 64, color: '#555' }} />
                                    </div>
                                )}

                                <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {detail.online ? (
                                        <Tag color="cyan" icon={<GlobalOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                            {detail.onlinePlatform ? `Online - ${detail.onlinePlatform}` : 'Online'}
                                        </Tag>
                                    ) : (
                                        <Tag color="geekblue" icon={<EnvironmentOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                            {t('invitations.inPerson', 'In-Person')}
                                        </Tag>
                                    )}

                                    {detail.status === 'SCHEDULED' && (
                                        <Tag color="blue" icon={<CalendarOutlined />} style={{ fontSize: 13, padding: '4px 10px', fontWeight: 'bold' }}>
                                            {t('invitations.status.scheduled', 'Scheduled')}
                                        </Tag>
                                    )}

                                    {hasUserJoinRequest && userJoinRequest && (
                                        <>
                                            {userJoinRequest.status === 'APPROVED' && (
                                                <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px', fontWeight: 'bold' }}>
                                                    {t('joinRequests.status.approved', 'Confirmed')}
                                                </Tag>
                                            )}
                                            {userJoinRequest.status === 'PENDING' && (
                                                <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px', fontWeight: 'bold' }}>
                                                    {t('joinRequests.status.pending', 'Pending Approval')}
                                                </Tag>
                                            )}
                                            {userJoinRequest.status === 'REJECTED' && (
                                                <Tag color="red" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px', fontWeight: 'bold' }}>
                                                    {t('joinRequests.status.rejected', 'Declined')}
                                                </Tag>
                                            )}
                                            {userJoinRequest.status === 'CANCELED' && (
                                                <Tag color="default" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px', fontWeight: 'bold' }}>
                                                    {t('joinRequests.status.canceled', 'Canceled')}
                                                </Tag>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col xs={24} md={14} style={{ padding: 24, display: 'flex', flexDirection: 'column' }}>
                            <Title level={2} style={{ margin: '0 0 12px 0' }}>
                                {detail.title}
                            </Title>

                            {/* GAME SYSTEM & ORGANIZER */}
                            <Space size="large" wrap style={{ marginBottom: 16 }}>
                                {detail.systemTitle && (
                                    <Flex align="center" gap="small">
                                        {detail.systemMediaUrl && (
                                            <Image src={detail.systemMediaUrl} preview={false} width={20} height={20} style={{ borderRadius: 3, objectFit: 'cover' }} />
                                        )}
                                        <Text strong>{detail.systemTitle}</Text>
                                    </Flex>
                                )}

                                {detail.organizerName && (
                                    <Flex align="center" gap="small">
                                        {detail.organizerLogoUrl ? (
                                            <Avatar src={detail.organizerLogoUrl} size="small" />
                                        ) : (
                                            <ReadOutlined />
                                        )}
                                        <Text type="secondary">{detail.organizerName}</Text>
                                    </Flex>
                                )}
                            </Space>

                            {/* KEY METRICS GRID */}
                            <Row gutter={[16, 16]} style={{ marginTop: 'auto', background: '#fafafa', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}>
                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.availableSeats', 'Available Seats')}
                                    </Text>
                                    <Tag color="green" icon={<TeamOutlined />} style={{ fontSize: 14, fontWeight: 'bold', margin: '4px 0 0 0' }}>
                                        {detail.availableSeats} / {detail.maxTotalPlayers}
                                    </Tag>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.costPerSession', 'Cost / Session')}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, color: '#1890ff', margin: '2px 0 0 0', display: 'block' }}>
                                        <DollarOutlined /> {detail.costPerSession != null ? `${detail.costPerSession} €` : 'Free'}
                                    </Text>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.sessions', 'Sessions')}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, margin: '2px 0 0 0', display: 'block' }}>
                                        <CalendarOutlined /> {detail.sessions?.length || 0}
                                    </Text>
                                </Col>

                                <Col xs={12} sm={6}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                        {t('invitations.deadline', 'Deadline')}
                                    </Text>
                                    <Text strong style={{ fontSize: 13, margin: '4px 0 0 0', display: 'block' }}>
                                        {dayjs(detail.invitationDeadline).format('YYYY-MM-DD HH:mm')}
                                    </Text>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card>

                {/* MAIN CONTENT DETAILS */}
                <Row gutter={[24, 24]}>
                    {/* LEFT COLUMN: Adventure Description & Content Warnings & Sessions */}
                    <Col xs={24} lg={16}>
                        {/* DESCRIPTION */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><BookOutlined /> {t('invitations.detail.description', 'Adventure Description')}</Title>}>
                            <Paragraph style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                {detail.description}
                            </Paragraph>

                            {/* CONTENT WARNINGS */}
                            {detail.contentWarnings && detail.contentWarnings.length > 0 && (
                                <>
                                    <Divider style={{ margin: '16px 0' }} />
                                    <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                                        <WarningOutlined style={{ color: '#fa8c16' }} /> {t('organizer.adventures.form.contentWarnings', 'Content Warnings')}
                                    </Title>
                                    <Space wrap size={[0, 8]}>
                                        {detail.contentWarnings.map(cw => (
                                            <Tag key={cw.id} color="orange" icon={<WarningOutlined />} style={{ fontSize: 13, padding: '4px 10px' }}>
                                                {t(cw.key || cw.id)}
                                            </Tag>
                                        ))}
                                    </Space>
                                </>
                            )}
                        </Card>

                        {/* SESSIONS SCHEDULE */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><CalendarOutlined /> {t('invitations.detail.sessionsSchedule', 'Sessions Schedule')}</Title>}>
                            {detail.sessions && detail.sessions.length > 0 ? (
                                <Timeline
                                    style={{ marginTop: 16 }}
                                    items={detail.sessions.map((s, idx) => ({
                                        color: 'blue',
                                        children: (
                                            <div>
                                                <Text strong>{t('invitations.detail.sessionNum', 'Session')} #{idx + 1}: </Text>
                                                <Text>{dayjs(s.time).format('dddd, MMMM D, YYYY [at] HH:mm')}</Text>
                                            </div>
                                        )
                                    }))}
                                />
                            ) : (
                                <Paragraph type="secondary">
                                    {t('invitations.detail.noSessions', 'No specific session dates scheduled yet.')}
                                </Paragraph>
                            )}
                        </Card>

                        {/* CANCELLATION POLICY & APPROVAL */}
                        <Card style={{ ...launcherCard }} title={<Title level={4} style={{ margin: 0 }}><InfoCircleOutlined /> {t('invitations.detail.policies', 'Policies & Rules')}</Title>}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {t('invitations.detail.cancelationPolicy', 'Cancellation Policy')}
                                </Text>
                                <Paragraph type="secondary" style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px dashed #d9d9d9' }}>
                                    {detail.cancelationPolicy || t('invitations.detail.noPolicy', 'Standard cancellation rules apply.')}
                                </Paragraph>
                            </div>

                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {t('invitations.detail.approvalMode', 'Player Approval')}
                                </Text>
                                {detail.approvalRequired ? (
                                    <Tag color="gold" icon={<SafetyCertificateOutlined />}>
                                        {t('invitations.detail.approvalRequired', 'GM Approval Required for Reservation')}
                                    </Tag>
                                ) : (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>
                                        {t('invitations.detail.instantBooking', 'Instant Reservation (Auto-approved)')}
                                    </Tag>
                                )}
                            </div>
                        </Card>
                    </Col>                    {/* RIGHT COLUMN: GM & PlaySpace & Organizer Info */}
                    <Col xs={24} lg={8}>
                        {/* GAME MASTER CARD */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}><UserOutlined /> {t('invitations.detail.gmTitle', 'Game Master')}</Title>}>
                            <Flex align="center" gap="middle" style={{ marginBottom: 16 }}>
                                <Avatar
                                    src={detail.gmPicture}
                                    icon={<UserOutlined />}
                                    size={56}
                                />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {detail.gmName || 'Anonymous GM'}
                                    </Title>
                                    {detail.gmNickname && (
                                        <Text type="secondary">@{detail.gmNickname}</Text>
                                    )}
                                </div>
                            </Flex>

                            <Divider style={{ margin: '14px 0' }} />

                            {/* PLAYER CAPACITY BREAKDOWN */}
                            <div>
                                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                                    <Text strong style={{ fontSize: 13 }}>
                                        <TeamOutlined style={{ marginRight: 6 }} />
                                        {t('invitations.detail.playerCapacity', 'Player Capacity')}
                                    </Text>
                                    <Tag
                                        color={isAtMaximum ? 'red' : isOverMinimum ? 'green' : 'gold'}
                                        style={{ margin: 0, fontWeight: 'bold' }}
                                    >
                                        {isAtMaximum
                                            ? t('invitations.detail.capacityFull', 'Table Full')
                                            : isOverMinimum
                                                ? t('invitations.detail.minimumMet', 'Min Reached')
                                                : t('invitations.detail.belowMinimum', 'Below Min')}
                                    </Tag>
                                </Flex>

                                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 14px' }}>
                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.verifiedPlayers', 'Confirmed / Joined')}:
                                        </Text>
                                        <Text strong style={{ fontSize: 13 }}>
                                            {verifiedPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.minPlayers', 'Minimum Required')}:
                                        </Text>
                                        <Text style={{ fontSize: 13 }}>
                                            {detail.minTotalPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    <Flex justify="space-between" style={{ marginBottom: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('invitations.detail.maxPlayers', 'Maximum Allowed')}:
                                        </Text>
                                        <Text style={{ fontSize: 13 }}>
                                            {detail.maxTotalPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                        </Text>
                                    </Flex>

                                    {detail.preexistingPlayers > 0 && (
                                        <Flex justify="space-between">
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {t('invitations.detail.preexistingPlayers', 'Pre-existing')}:
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {detail.preexistingPlayers} {t('invitations.detail.playersSuffix', 'players')}
                                            </Text>
                                        </Flex>
                                    )}

                                    <Progress
                                        percent={Math.min(100, Math.round((verifiedPlayers / detail.maxTotalPlayers) * 100))}
                                        status={isAtMaximum ? 'exception' : isOverMinimum ? 'success' : 'normal'}
                                        strokeColor={isAtMaximum ? '#ff4d4f' : isOverMinimum ? '#52c41a' : '#faad14'}
                                        style={{ marginTop: 8 }}
                                    />

                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        {isAtMaximum ? (
                                            <Text type="danger">
                                                <CheckCircleOutlined /> {t('invitations.detail.tableFullDesc', 'Maximum capacity reached. New requests go to waitlist.')}
                                            </Text>
                                        ) : isOverMinimum ? (
                                            <Text style={{ color: '#389e0d' }}>
                                                <CheckCircleOutlined /> {t('invitations.detail.minMetDesc', 'Minimum players reached! Adventure is ready to run.')}
                                            </Text>
                                        ) : (
                                            <Text type="warning">
                                                <InfoCircleOutlined /> {t('invitations.detail.needMorePlayers', 'Needs {{count}} more to reach minimum.', { count: detail.minTotalPlayers - verifiedPlayers })}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* PLAY SPACE & AMENITIES CARD */}
                        <Card style={{ ...launcherCard, marginBottom: 24 }} title={<Title level={4} style={{ margin: 0 }}>{detail.online ? <GlobalOutlined /> : <EnvironmentOutlined />} {t('invitations.detail.playSpaceTitle', 'Play Space')}</Title>}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                    {detail.online ? t('invitations.detail.platform', 'Online Platform') : t('invitations.detail.address', 'Location Address')}
                                </Text>
                                {detail.online ? (
                                    <Tag color="cyan" icon={<GlobalOutlined />} style={{ fontSize: 13 }}>
                                        {detail.onlinePlatform || 'Online'}
                                    </Tag>
                                ) : (
                                    <div>
                                        <Paragraph style={{ margin: '4px 0' }}>
                                            {detail.addressGmapsFormatted || t('invitations.detail.locationProvidedOnBooking', 'Location details provided upon reservation.')}
                                        </Paragraph>
                                        {detail.addressGmapsUrl && (
                                            <a href={detail.addressGmapsUrl} target="_blank" rel="noopener noreferrer">
                                                <EnvironmentOutlined /> {t('invitations.detail.openGmaps', 'View on Google Maps')}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            {detail.accessInfo && (
                                <div style={{ marginBottom: 16 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                        {t('invitations.detail.accessInfo', 'Access Information')}
                                    </Text>
                                    <Paragraph type="secondary" style={{ fontSize: 13 }}>
                                        {detail.accessInfo}
                                    </Paragraph>
                                </div>
                            )}

                            {/* AMENITIES */}
                            <Divider style={{ margin: '12px 0' }} />
                            <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                                {t('invitations.detail.amenities', 'Space Features & Rules')}
                            </Title>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <AmenityItem label={t('invitations.amenities.wheelchair', 'Wheelchair Accessible')} active={detail.wheelchairAccess} />
                                <AmenityItem label={t('invitations.amenities.canEatIn', 'Food Allowed')} active={detail.canEatIn} />
                                <AmenityItem label={t('invitations.amenities.canDrinkIn', 'Drinks Allowed')} active={detail.canDrinkIn} />
                                <AmenityItem label={t('invitations.amenities.canSmoke', 'Smoking Allowed')} active={detail.canSmoke} />
                                <AmenityItem label={t('invitations.amenities.publicSpace', 'Public Space')} active={detail.publicSpace} />
                            </Space>
                        </Card>

                        {/* ORGANIZER CARD */}
                        {detail.organizerName && (
                            <Card style={{ ...launcherCard }} title={<Title level={4} style={{ margin: 0 }}><ReadOutlined /> {t('invitations.detail.organizerTitle', 'Organized By')}</Title>}>
                                <Flex align="center" gap="small" style={{ marginBottom: 12 }}>
                                    {detail.organizerLogoUrl && (
                                        <Avatar src={detail.organizerLogoUrl} size="large" />
                                    )}
                                    <Title level={5} style={{ margin: 0 }}>{detail.organizerName}</Title>
                                </Flex>
                                {detail.organizerDescription && (
                                    <Paragraph type="secondary" style={{ fontSize: 13, margin: 0 }}>
                                        {detail.organizerDescription}
                                    </Paragraph>
                                )}
                            </Card>
                        )}
                    </Col>
                </Row>

                {/* RESERVATION CALL TO ACTION / ORGANIZER JOIN REQUESTS LINK SECTION */}
                {isOrganizer ? (
                    <Card style={{ ...launcherCard, marginTop: 24, padding: '20px 24px', textAlign: 'center' }}>
                        <Flex vertical align="center" gap="middle">
                            <Title level={4} style={{ margin: 0 }}>
                                <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                {t('organizer.joinRequests.heading', 'Join Requests')}
                            </Title>

                            {detail.status === 'SCHEDULED' ? (
                                <Alert
                                    type="success"
                                    showIcon
                                    message={t('organizer.invitations.scheduledAlertTitle', 'Adventure is Scheduled')}
                                    description={t('organizer.invitations.scheduledAlertDesc', 'This adventure invitation is scheduled. All approved players have reserved seats for the sessions.')}
                                    style={{ textAlign: 'left', width: '100%', maxWidth: 600 }}
                                />
                            ) : (
                                <Paragraph style={{ margin: 0, color: '#666', fontSize: 14 }}>
                                    {t('organizer.invitations.organizerCtaDesc', 'You are organizing this adventure. View and manage player join requests and RSVPs.')}
                                </Paragraph>
                            )}

                            <Flex gap="middle" wrap="wrap" justify="center">
                                <Button
                                    type="default"
                                    size="large"
                                    icon={<TeamOutlined />}
                                    onClick={() => navigate(`/organizer/invitations/${detail.id}/join-requests`)}
                                    style={{ minWidth: 200 }}
                                >
                                    {t('organizer.joinRequests.title', 'Join Requests')}
                                </Button>

                                <ScheduleInvitationButton
                                    invitationId={detail.id}
                                    status={detail.status}
                                    sessionsCount={detail.sessions?.length}
                                />
                            </Flex>
                        </Flex>
                    </Card>
                ) : isUserLoggedIn() && isUserJoinRequestLoading ? (
                    <Card style={{ ...launcherCard, marginTop: 24, padding: '28px 24px', textAlign: 'center' }}>
                        <Spin size="large" />
                    </Card>
                ) : (
                    <Card style={{ ...launcherCard, marginTop: 24, padding: '16px 24px' }}>
                        <ReservationCtaSection
                            canAcceptReservations={true}
                            hasUserAlreadyReserved={hasUserJoinRequest}
                            isHostOrGm={isGm}
                            hostOrGmContent={
                                <Alert
                                    type="info"
                                    showIcon
                                    message={t('reservation.cta.hostOrGmTitle', 'Adventure Host / Game Master')}
                                    description={t('reservation.cta.hostOrGm', 'You are the Game Master of this adventure, so you cannot reserve a seat as a player.')}
                                />
                            }
                            alreadyReservedContent={
                                userJoinRequest && (
                                    <PlayerJoinRequestStatusDisplay
                                        request={userJoinRequest}
                                        onViewDetail={() => navigate(`/join-requests/${userJoinRequest.id}`)}
                                    />
                                )
                            }
                        >
                            {!showBookingForm ? (
                                <div>
                                    <Label strong trl={'reservation.buttons.reserveSeat'} />
                                    <div style={{ marginTop: 12 }}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            style={{ width: '100%', backgroundColor: 'limegreen', borderColor: 'limegreen' }}
                                            onClick={() => setShowBookingForm(true)}
                                        >
                                            {t('reservation.buttons.reserveSeat', 'Reserve a seat')}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <SessionRsvpBookingForm
                                    invitation={detail}
                                    onSuccess={(jr) => {
                                        refetchUserJoinRequest();
                                        navigate(`/join-requests/${jr.id}`);
                                    }}
                                    onCancel={() => setShowBookingForm(false)}
                                />
                            )}
                        </ReservationCtaSection>
                    </Card>
                )}
            </div>
        </LauncherLayout>
    );
};

interface PlayerJoinRequestStatusDisplayProps {
    request: JoinRequestDto;
    onViewDetail: () => void;
}

const PlayerJoinRequestStatusDisplay: React.FC<PlayerJoinRequestStatusDisplayProps> = ({ request, onViewDetail }) => {
    const { t } = useTranslation();
    const isApproved = request.status === 'APPROVED';
    const isPending = request.status === 'PENDING';
    const isRejected = request.status === 'REJECTED';
    const isCanceled = request.status === 'CANCELED';

    const statusBg = isApproved ? '#f6ffed' : isPending ? '#fffbe6' : isRejected ? '#fff2f0' : '#f5f5f5';
    const statusBorder = isApproved ? '#b7eb8f' : isPending ? '#ffe58f' : isRejected ? '#ffccc7' : '#d9d9d9';

    const sortedRsvps = useMemo(() => {
        if (!request.sessionRsvps) return [];
        return [...request.sessionRsvps].sort((a, b) => {
            if (a.sessionTime && b.sessionTime) return a.sessionTime - b.sessionTime;
            if (a.sessionTime) return -1;
            if (b.sessionTime) return 1;
            return 0;
        });
    }, [request.sessionRsvps]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* STATUS HEADER BANNER */}
            <div
                style={{
                    background: statusBg,
                    border: `1px solid ${statusBorder}`,
                    borderRadius: 8,
                    padding: '16px 20px'
                }}
            >
                <Flex justify="space-between" align="start" wrap="wrap" gap="small">
                    <Space size="middle" align="start">
                        {isApproved && <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a', marginTop: 2 }} />}
                        {isPending && <ClockCircleOutlined style={{ fontSize: 24, color: '#faad14', marginTop: 2 }} />}
                        {isRejected && <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f', marginTop: 2 }} />}
                        {isCanceled && <CloseCircleOutlined style={{ fontSize: 24, color: '#999', marginTop: 2 }} />}
                        <div>
                            <Text strong style={{ fontSize: 16, display: 'block' }}>
                                {isApproved && t('joinRequests.status.approvedTitle', 'Seat Reservation Confirmed!')}
                                {isPending && t('joinRequests.status.pendingTitle', 'Join Request Pending Approval')}
                                {isRejected && t('joinRequests.status.rejectedTitle', 'Join Request Declined')}
                                {isCanceled && t('joinRequests.status.canceledTitle', 'Join Request Canceled')}
                            </Text>
                            <Paragraph type="secondary" style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                                {isApproved && t('invitations.detail.alreadyReservedApproved', 'You have a confirmed reservation for this adventure.')}
                                {isPending && t('invitations.detail.alreadyReservedPending', 'You have submitted a join request that is awaiting review.')}
                                {isRejected && t('invitations.detail.alreadyReservedRejected', 'Your previous join request was not accepted.')}
                                {isCanceled && t('invitations.detail.alreadyReservedCanceled', 'Your join request for this adventure was canceled.')}
                            </Paragraph>
                        </div>
                    </Space>

                    <div>
                        {isApproved && <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>{t('joinRequests.status.approved', 'Confirmed')}</Tag>}
                        {isPending && <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>{t('joinRequests.status.pending', 'Pending')}</Tag>}
                        {isRejected && <Tag color="red" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>{t('joinRequests.status.rejected', 'Declined')}</Tag>}
                        {isCanceled && <Tag color="default" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>{t('joinRequests.status.canceled', 'Canceled')}</Tag>}
                    </div>
                </Flex>
            </div>

            {/* SESSIONS & RSVP AVAILABILITY BREAKDOWN */}
            {sortedRsvps.length > 0 && (
                <div>
                    <Text strong style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 8 }}>
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        {t('joinRequests.detail.sessionsAndRsvp', 'Your Session Availability')}:
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {sortedRsvps.map((s, idx) => (
                            <Flex
                                key={s.sessionId || idx}
                                justify="space-between"
                                align="center"
                                style={{
                                    background: '#fafafa',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 6,
                                    padding: '8px 14px',
                                    fontSize: 13
                                }}
                            >
                                <span>
                                    <Text strong>{t('invitations.detail.sessionNum', 'Session')} #{idx + 1}: </Text>
                                    <Text>{s.sessionTime ? dayjs(s.sessionTime).format('dddd, MMMM D, YYYY [at] HH:mm') : t('invitations.detail.tbd', 'TBD')}</Text>
                                </span>
                                <div>
                                    {s.rsvp === 'YES' && (
                                        <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
                                            {t('invitations.rsvp.yes', 'YES')}
                                        </Tag>
                                    )}
                                    {s.rsvp === 'MAYBE' && (
                                        <Tag color="gold" icon={<QuestionCircleOutlined />} style={{ margin: 0 }}>
                                            {t('invitations.rsvp.maybe', 'MAYBE')}
                                        </Tag>
                                    )}
                                    {s.rsvp === 'NO' && (
                                        <Tag color="default" icon={<CloseCircleOutlined />} style={{ margin: 0 }}>
                                            {t('invitations.rsvp.no', 'NO')}
                                        </Tag>
                                    )}
                                </div>
                            </Flex>
                        ))}
                    </div>
                </div>
            )}

            {/* PLAYER NOTE (IF ANY) */}
            {request.playerNote && (
                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '10px 14px' }}>
                    <Text strong style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                        {t('joinRequests.detail.yourNote', 'Your Note to the Game Master')}:
                    </Text>
                    <Text style={{ fontSize: 13, color: '#333', fontStyle: 'italic' }}>
                        "{request.playerNote}"
                    </Text>
                </div>
            )}

            {/* ORGANIZER RESPONSE NOTE (IF ANY) */}
            {request.organizerResponseNote && (
                <div style={{
                    background: isApproved ? '#f6ffed' : '#fff2f0',
                    border: `1px solid ${isApproved ? '#b7eb8f' : '#ffccc7'}`,
                    borderRadius: 6,
                    padding: '10px 14px'
                }}>
                    <Text strong style={{ fontSize: 12, color: isApproved ? '#389e0d' : '#cf1322', display: 'block', marginBottom: 4 }}>
                        {t('joinRequests.detail.organizerResponse', 'Organizer / GM Response')}:
                    </Text>
                    <Text style={{ fontSize: 13, color: '#333' }}>
                        {request.organizerResponseNote}
                    </Text>
                </div>
            )}

            {/* TIMESTAMPS & ACTIONS */}
            <Flex justify="space-between" align="center" wrap="wrap" gap="middle" style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('joinRequests.detail.submittedOn', 'Submitted on')}: {dayjs(request.createdAt).format('YYYY-MM-DD HH:mm')}
                </Text>

                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={onViewDetail}
                    style={{ minWidth: 200 }}
                >
                    {t('invitations.detail.viewJoinRequest', 'View Your Join Request')}
                </Button>
            </Flex>
        </div>
    );
};

const AmenityItem: React.FC<{ label: string; active: boolean }> = ({ label, active }) => (
    <Flex justify="space-between" align="center" style={{ fontSize: 13 }}>
        <span>{label}</span>
        {active ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
        ) : (
            <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
        )}
    </Flex>
);
